import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { isExampleResource } from '@gonfalon/analytics/isExampleResource';
import { useDebounce } from '@gonfalon/async/useDebounce';
import { trackReleaseStrategyBackClicked } from '@gonfalon/automated-rollouts/analytics';
import { approvalsNotificationListMaxLimit, enableNewReviewSaveModal } from '@gonfalon/dogfood-flags/dogfood-flags';
import { pluralize } from '@gonfalon/format/pluralize';
import { InPortal } from '@gonfalon/launchpad-experimental/CustomSelect';
import { AutosizeTextArea, Form, FormField } from '@gonfalon/legacy-forms/components';
import { useGuidanceStepProps, useSaveGuidanceSteps } from '@gonfalon/onboarding-guidance/GuidanceInitialValidContext';
import { type RoleName } from '@gonfalon/permissions/internal/types';
import { formattedRoleName } from '@gonfalon/permissions/role';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import { Button } from '@launchpad-ui/components';
// oxlint-disable-next-line no-restricted-imports
import { AbsoluteModalFooter, Checkbox, RequiredAsterisk, TextField, Tooltip } from '@launchpad-ui/core';
import cx from 'clsx';

import { useAiConfigApprovalSettings } from '#components/AiConfigs/AiConfigApprovals/useAiConfigApprovalSettings';
import CommentArea from '#components/forms/CommentArea';
import { InviteMembersOption } from '#components/inviteMembers/InviteMembersOption';
import type { IntegrationConfig } from '#components/PendingChanges/ApprovalIntegrationFields';
import { ApprovalIntegrationFields } from '#components/PendingChanges/ApprovalIntegrationFields';
import { PendingChangesActions } from '#components/PendingChanges/PendingChangesActions';
import RequiredFieldsNotice from '#components/RequiredFieldsNotice';
import { useExternalState } from '#hooks/useExternalState';
import { timedFlagTargetingEvents, trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';
import type { Member } from '#utils/members/records/Member';
import { validateNotifyReviewersCount } from '#utils/pendingChanges/validateNotifyReviewersCount';
import { SaveOptions } from '#utils/saveButtonUtils';
import type { Team } from '#utils/teams/records/Team';

import { SelectReviewersContainer } from './FormSection/Approvals/SelectReviewersContainer';
import { NewPendingChangesFormContainer } from './NewPendingChangesForm/NewPendingChangesForm';
import { usePendingChangesContext } from './PendingChangesContext';
import { PendingChangesDatePopover } from './PendingChangesDatePopover';
import type { PendingChangesFormProps } from './pendingChangesFormTypes';

// oxlint-disable-next-line no-unassigned-import
import './styles.css';

function OriginalPendingChangesForm({
  hideScheduleOption,
  hideApprovalOption,
  saveOption,
  pendingChangesFormState,
  resourceValidation,
  approvalServiceKind,
  approvalServiceManifest,
  flagKey,
  isUserPendingChanges,
  useBackButton,
  absoluteModalFooterClassName,
  isAIFlag,
  onEditPendingChangesForm,
  onBlur,
  onBack,
  onCancel,
  onSubmit,
}: PendingChangesFormProps) {
  const { environment, enableFilterByApprovers } = usePendingChangesContext();

  // Legacy code (approval settings used to only be based on environment)
  const envApprovalSettings = environment.get('approvalSettings');

  // AI config (approval settings can now be different for AI configs)
  const aiConfigApprovalSettings = useAiConfigApprovalSettings({
    projectKey: useProjectKey(),
    environmentKey: environment.key,
  });

  const { minNumberOfApprovals, canReviewOwnRequest } = useMemo(() => {
    if (isAIFlag) {
      return {
        minNumberOfApprovals: aiConfigApprovalSettings?.currentEnvironment.minNumApprovals,
        canReviewOwnRequest: aiConfigApprovalSettings?.currentEnvironment.canReviewOwnRequest,
      };
    }

    return {
      minNumberOfApprovals: envApprovalSettings?.get('minNumApprovals'),
      canReviewOwnRequest: envApprovalSettings?.get('canReviewOwnRequest'),
    };
  }, [isAIFlag, aiConfigApprovalSettings, envApprovalSettings]);

  const pendingChangesForm = pendingChangesFormState.get('modified');
  const handleSelectReviewers = (members?: Member[], teams?: Team[]) => {
    // oxlint-disable-next-line no-extra-boolean-cast
    const newMemberReviewers = !!members ? members : [];
    // oxlint-disable-next-line no-extra-boolean-cast
    const newTeamReviewers = !!teams ? teams : undefined;
    onEditPendingChangesForm('reviewers', [...newMemberReviewers, ...(newTeamReviewers || [])]);
    onEditPendingChangesForm('notifyMembers', newMemberReviewers);
    newTeamReviewers && onEditPendingChangesForm('notifyTeams', newTeamReviewers);
  };
  const filterOutCurrentMember = !canReviewOwnRequest;
  const usesLaunchDarklyApprovalService = pendingChangesForm.get('usesLaunchDarklyApprovalService');
  const isConfirmationRequired = pendingChangesForm.get('isConfirmationRequired');
  const isCommentRequired = pendingChangesForm.get('isCommentRequired');
  const isAcknowledgeConflictsRequired = pendingChangesForm.get('isAcknowledgeConflictsRequired');
  const isScheduleSelected = pendingChangesForm.get('isScheduleSelected');
  const isApprovalSelected = pendingChangesForm.get('isApprovalSelected');
  const isLoadingConflicts = pendingChangesForm.get('isLoadingConflicts');
  const showCommentField = !isApprovalSelected && saveOption !== SaveOptions.DELETE;
  const { confirmationGuidanceStep } = useSaveGuidanceSteps();
  const guidanceStepProps = useGuidanceStepProps(confirmationGuidanceStep);
  const hasRequiredFields =
    isApprovalSelected || isConfirmationRequired || isCommentRequired || isAcknowledgeConflictsRequired;

  const formIsInvalid = hasRequiredFields
    ? !pendingChangesFormState.canSubmitForm()
    : !pendingChangesFormState.canSubmitOptionalForm();

  const footerCTA =
    saveOption === SaveOptions.REQUEST_APPROVAL_AND_SCHEDULE ? SaveOptions.REQUEST_APPROVAL : saveOption;

  const handleSubmit = (event: FormEvent<EventTarget>) => {
    event.preventDefault();

    if (formIsInvalid) {
      return;
    }

    onSubmit(pendingChangesForm);
  };

  const handleCancel = () => {
    trackFlagTargetingEvent('Review And Save Cancel Button Clicked', { saveOption, flagKey });
    onCancel();
  };

  const handleBack = useCallback(() => {
    trackReleaseStrategyBackClicked();
    onBack?.();
  }, [onBack]);

  const onResourceConfirmationChange = useDebounce((event: React.ChangeEvent<HTMLInputElement>) => {
    onEditPendingChangesForm('resourceConfirmation', event.target.value);
  }, 150);

  const customInviteMembersOption = (
    <InviteMembersOption
      onClick={() => onEditPendingChangesForm('isInviteMemberSelected', true)}
      component="PendingChangesForm"
      inviteMembersModalProps={{
        referrer: 'PendingChanges',
        defaultRole: 'admin',
        renderAlertForRoles: {
          roles: ['reader', 'no_access'],
          renderAlertMessage: (selectedRole: RoleName) =>
            `Members with the ${formattedRoleName(
              selectedRole,
            ).toLowerCase()} role cannot review flag approval requests.`,
        },
        onClose: () => onEditPendingChangesForm('isInviteMemberSelected', false),
      }}
    />
  );

  return (
    <Form
      id="search-pending-changes" // adding search to id and name to prevent lastpass adding in autofill icon
      name="search-pending-changes"
      className="PendingChangesForm"
      onSubmit={handleSubmit}
    >
      {!(hideScheduleOption && hideApprovalOption) &&
        !isAIFlag &&
        (saveOption === SaveOptions.UPDATE_SCHEDULE ? (
          <PendingChangesDatePopover
            className="PendingChangesActions"
            date={pendingChangesForm.executionDate ? new Date(pendingChangesForm.executionDate) : undefined}
            onSetDate={(date: number) => {
              onEditPendingChangesForm('executionDate', date);
            }}
            useLongDate
          />
        ) : (
          <PendingChangesActions
            hideScheduleOption={hideScheduleOption}
            hideApprovalOption={hideApprovalOption}
            isScheduleSelected={isScheduleSelected}
            executionDate={pendingChangesForm.executionDate}
            isApprovalSelected={isApprovalSelected}
            onEditPendingChangesForm={onEditPendingChangesForm}
          />
        ))}
      <section
        id="approval-region"
        className="PendingChangesForm-formFields"
        aria-labelledby={isApprovalSelected && !hideApprovalOption ? 'approval-accordion' : undefined}
      >
        {hasRequiredFields && <RequiredFieldsNotice />}
        {isApprovalSelected && (
          <FormField
            isRequired
            className={cx({
              'PendingChangesForm-approvalDescription--approvalOptionallySelected':
                isApprovalSelected && !hideApprovalOption,
            })}
            label="Reason"
            name="description"
            htmlFor="description"
            hint="Add a brief description of your changes."
            formState={pendingChangesFormState}
            onBlur={onBlur}
          >
            <DescriptionField
              value={pendingChangesForm.description}
              onChange={(description) => onEditPendingChangesForm('description', description)}
            />
          </FormField>
        )}
        {isApprovalSelected && usesLaunchDarklyApprovalService && (
          <FormField
            isRequired
            label="Reviewers"
            name="notifyMembers"
            htmlFor="notifyMembers"
            hint={
              minNumberOfApprovals
                ? `Minimum ${minNumberOfApprovals} ${pluralize(
                    minNumberOfApprovals,
                    'approval',
                    'approvals',
                  )} required to apply these changes`
                : 'Select at least 1 reviewer to be notified.'
            }
            formState={pendingChangesFormState}
            onBlur={onBlur}
          >
            <InPortal>
              <SelectReviewersContainer
                id="notifyMembers"
                resourceKind="FlagConfiguration"
                resourceKey={flagKey}
                useAccessCheck={enableFilterByApprovers && !isUserPendingChanges}
                showGroupOptions={enableFilterByApprovers && !isUserPendingChanges}
                value={[...(pendingChangesForm.notifyTeams || []), ...pendingChangesForm.notifyMembers]}
                filterOutCurrentMember={filterOutCurrentMember}
                onChange={handleSelectReviewers}
                customNoOptionsMessage={customInviteMembersOption}
              />
            </InPortal>
          </FormField>
        )}

        {showCommentField && (
          <FormField
            isRequired={isCommentRequired}
            label="Comment"
            name="comment"
            htmlFor="comment"
            formState={pendingChangesFormState}
            onBlur={onBlur}
          >
            <CommentField
              value={pendingChangesForm.comment}
              onChange={(newComment) => {
                onEditPendingChangesForm('comment', newComment);
              }}
            />
          </FormField>
        )}
        {isApprovalSelected && approvalServiceManifest && approvalServiceKind && (
          <ApprovalIntegrationFields
            manifest={approvalServiceManifest}
            approvalServiceKind={approvalServiceKind}
            onChange={(integrationConfig: IntegrationConfig) => {
              onEditPendingChangesForm('integrationConfig', integrationConfig);
            }}
          />
        )}
        {!isApprovalSelected && isConfirmationRequired && (
          <FormField
            isRequired
            label="Confirm"
            name="resourceConfirmation"
            htmlFor="resourceConfirmation"
            hint={resourceValidation.label}
            formState={pendingChangesFormState}
            onBlur={onBlur}
          >
            <TextField
              placeholder={resourceValidation.placeholder}
              onChange={onResourceConfirmationChange}
              id="resourceConfirmation"
              autoComplete="off"
            />
          </FormField>
        )}
        {!isApprovalSelected && !isLoadingConflicts && isAcknowledgeConflictsRequired && (
          <FormField
            className="PendingChangesForm-acknowledgeConflicts"
            isRequired
            name="acknowledgeConflicts"
            htmlFor="acknowledgeConflicts"
            formState={pendingChangesFormState}
            onBlur={onBlur}
          >
            <Checkbox
              checked={pendingChangesForm.acknowledgeConflicts}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onEditPendingChangesForm('acknowledgeConflicts', event.target.checked);
              }}
            >
              <p>
                I acknowledge saving this change will introduce conflicts with pending changes
                <RequiredAsterisk />
              </p>
            </Checkbox>
          </FormField>
        )}
      </section>
      <AbsoluteModalFooter
        className={absoluteModalFooterClassName}
        primaryButton={
          <Tooltip
            content={
              hasRequiredFields &&
              validateNotifyReviewersCount(
                approvalsNotificationListMaxLimit(),
                pendingChangesForm.get('notifyTeams'),
                pendingChangesForm.get('notifyMembers'),
              ) &&
              !pendingChangesFormState.canSubmitForm()
                ? `You may only specify at most ${approvalsNotificationListMaxLimit()} reviewers`
                : ''
            }
          >
            <Button
              {...guidanceStepProps}
              data-guidance-valid={pendingChangesFormState.canSubmitForm()}
              type="submit"
              variant={saveOption === SaveOptions.DELETE ? 'destructive' : 'primary'}
              data-test-id="submit-pending-changes-btn"
              onPress={() => {
                trackFlagTargetingEvent
                  .measure(timedFlagTargetingEvents.TIME_TO_SAVE_FLAG_CONFIGURATION, {
                    throwErrorIfTimerNotStarted: false,
                  })
                  .track('Review And Save Submit Button Clicked', {
                    saveOption,
                    flagKey,
                    isExampleResource: isExampleResource(flagKey),
                  })
                  .restart();
              }}
              isDisabled={formIsInvalid}
            >
              {footerCTA}
            </Button>
          </Tooltip>
        }
        secondaryButton={
          useBackButton ? (
            <Button onPress={handleBack} variant="default">
              Back
            </Button>
          ) : (
            <Button onPress={handleCancel} variant="default">
              Cancel
            </Button>
          )
        }
      />
    </Form>
  );
}

function DescriptionField({ value, onChange }: { value: string; onChange(newValue: string): void }) {
  const [inputValue, setInputValue] = useExternalState(value);

  return (
    <AutosizeTextArea
      maxRows={4}
      id="description"
      name="description"
      rows={1}
      onBlur={() => {
        onChange(inputValue);
      }}
      onChange={(event) => {
        const nextValue = event.target.value;
        setInputValue(nextValue);
        if (Boolean(inputValue) !== Boolean(nextValue)) {
          onChange(nextValue);
        }
      }}
      value={inputValue}
    />
  );
}

function CommentField({ value, onChange }: { value: string; onChange(newValue: string): void }) {
  const [inputValue, setInputValue] = useExternalState(value);
  const debouncedOnChange = useDebounce(onChange, 150);

  return (
    <CommentArea
      aria-label="Comment"
      value={inputValue}
      onBlur={() => {
        onChange(inputValue);
      }}
      onChange={(nextValue) => {
        setInputValue(nextValue);
        debouncedOnChange(nextValue);
      }}
    />
  );
}

export function PendingChangesForm(props: PendingChangesFormProps) {
  useEffect(() => {
    trackFlagTargetingEvent.start(timedFlagTargetingEvents.TIME_ON_REVIEW_AND_SAVE);

    return () => {
      trackFlagTargetingEvent.clear(timedFlagTargetingEvents.TIME_ON_REVIEW_AND_SAVE);
    };
  }, []);

  if (enableNewReviewSaveModal()) {
    return <NewPendingChangesFormContainer {...props} />;
  }

  return <OriginalPendingChangesForm {...props} />;
}
